import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useMatchEventsSocket } from '../../hooks/useMatchEventsSocket';
import { useMatchEventState } from '../../api/useMatchEvents';
import { secondsToClock, pickFresherState } from '../../utils/matchEventsFeed';
import { formatStateLine, groupBySubgroup } from '../../utils/sniperArm';
import {
  useActivateSniperEvent,
  useDeactivateSniperEvent,
  usePatchSniperMarket,
  useDeleteSniperMarket,
} from '../../api/useSniper';
import type { SniperEvent } from '../../types/sniper.types';
import { SniperMarketRow } from './SniperMarketRow';
import { SniperImportDialog } from './SniperImportDialog';
import { SeedScoreControl } from '../matchEvents/SeedScoreControl';
import { VarReviewBanner } from '../matchEvents/VarReviewBanner';

/**
 * One event (match) card: the live state line (score + `Corners H–A` + clock),
 * an Active toggle (reuses the `/match-events` feed active set), an import-by-link
 * control, and the event's markets subgrouped by trigger type (Gol / Corner).
 *
 * The score/corners/clock/VAR come from the BACKEND-authored `MatchState`
 * (`match_state` WS event + `GET /match-events/state/:fixtureId` backfill) — the
 * FE renders it and derives nothing. The live WS value supersedes the REST
 * backfill; the same `MatchState` drives each row's ARMED badge.
 */
export function SniperEventCard({ event }: { event: SniperEvent }) {
  const [importOpen, setImportOpen] = useState(false);

  const activate = useActivateSniperEvent();
  const deactivate = useDeactivateSniperEvent();
  const patchMarket = usePatchSniperMarket();
  const deleteMarket = useDeleteSniperMarket();

  // Only an ACTIVE event streams — subscribing to an inactive fixture joins a
  // room with no feed attached.
  const subscribeFixtureId = event.active ? event.fixtureId : null;
  const { matchState } = useMatchEventsSocket({
    fixtureIds: subscribeFixtureId != null ? [subscribeFixtureId] : [],
  });
  const { data: backfill } = useMatchEventState(subscribeFixtureId);

  // Backend-authored MatchState: prefer whichever of {live WS, REST backfill}
  // carries the NEWER `ts` — so a stale live value can't permanently shadow a
  // fresher REST snapshot recovered after a reconnect. The FE RENDERS this — it
  // never derives score/corners itself.
  const state = useMemo(() => {
    if (!event.active || subscribeFixtureId == null) return null;
    const live = matchState.get(subscribeFixtureId);
    return pickFresherState(live, backfill);
  }, [event.active, subscribeFixtureId, matchState, backfill]);

  const clockText = secondsToClock(state?.clock ?? null);
  // No backend MatchState for an ACTIVE fixture = the baseline has not been
  // established yet (feed cold-started mid-match). Show an explicit "score
  // syncing…" so the operator knows WHY the board is blank and that a seed is
  // available — NEVER a fabricated 0–0. An inactive event just shows the teams.
  const syncing = event.active && !state;
  const stateLine = state
    ? formatStateLine(state, event.homeTeam, event.awayTeam, clockText)
    : syncing
      ? `${event.homeTeam} vs ${event.awayTeam} · score syncing…`
      : `${event.homeTeam} vs ${event.awayTeam}`;

  const subgroups = useMemo(
    () => groupBySubgroup(event.markets ?? []),
    [event.markets],
  );

  const toggleActive = (next: boolean) => {
    if (next) activate.mutate(event.fixtureId);
    else deactivate.mutate(event.fixtureId);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      {/* `useFlexGap` is load-bearing, not cosmetic: without it MUI implements
          `spacing` as a margin-left on siblings, so a wrapped row gets ZERO
          row-gap (line 2 sits flush against line 1) plus a stray indent. This row
          does wrap once the VAR banner appears, which would otherwise relocate the
          Active kill-switch mid-match. */}
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        useFlexGap
        sx={{ flexWrap: 'wrap' }}
      >
        <Typography variant="subtitle1" sx={{ color: 'common.white', fontWeight: 600 }}>
          {stateLine}
        </Typography>
        {event.league && (
          <Chip size="small" variant="outlined" label={event.league} />
        )}
        {event.live && <Chip size="small" color="success" label="live" />}
        {state?.lastVarActive && <VarReviewBanner />}
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          startIcon={<UploadFileIcon />}
          onClick={() => setImportOpen(true)}
        >
          Import markets
        </Button>
        <FormControlLabel
          control={
            <Switch
              checked={event.active}
              onChange={(e) => toggleActive(e.target.checked)}
              inputProps={{ 'aria-label': 'activate event feed' }}
            />
          }
          label={event.active ? 'Active' : 'Inactive'}
        />
      </Stack>

      {/* Operator seed: establish the baseline when it is syncing, or correct an
          existing one (the feed can mis-state a score — override allowed). The
          engine re-publishes the authoritative MatchState; the board renders
          from that, never from the typed values. */}
      {event.active && (
        <Box sx={{ mt: 1 }}>
          <SeedScoreControl
            fixtureId={event.fixtureId}
            homeTeam={event.homeTeam}
            awayTeam={event.awayTeam}
            mode={state ? 'override' : 'seed'}
          />
        </Box>
      )}

      <Divider sx={{ my: 1.5 }} />

      {subgroups.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No markets yet — use “Import markets” to add some.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {subgroups.map((group) => (
            <Box key={group.subgroup}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                {group.label}
              </Typography>
              <Stack spacing={0.75}>
                {group.rows.map((market) => (
                  <SniperMarketRow
                    key={market._id}
                    market={market}
                    state={state}
                    busy={patchMarket.isLoading || deleteMarket.isLoading}
                    onPatch={(patch) =>
                      patchMarket.mutate({ marketId: market._id, patch })
                    }
                    onDelete={() => deleteMarket.mutate(market._id)}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      <SniperImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        fixtureId={event.fixtureId}
        homeTeam={event.homeTeam}
        awayTeam={event.awayTeam}
      />
    </Paper>
  );
}
